import React, { useState, useEffect, useRef } from 'react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import DatasetSelector from '../../Common/DatasetSelector';
import DiscretizationLayout from '../../Common/DiscretizationLayout';
import { Box, Paper, Typography, Button, Chip, TextField, MenuItem, Select as MuiSelect, FormControl, InputLabel, CircularProgress } from '@mui/material';

const LineChart = ({ plotData, defaultTitle, xlabel, ylabel, titleKey, showMrpLine = false }) => {
  const plotRef = useRef(null);
  const [title, setTitle] = useState(() => localStorage.getItem(`mrp_numbers_title_${titleKey}`) || defaultTitle || '');
  const [xLabel, setXLabel] = useState(() => localStorage.getItem(`mrp_numbers_xlabel_${titleKey}`) || xlabel || '');
  const [yLabel, setYLabel] = useState(() => localStorage.getItem(`mrp_numbers_ylabel_${titleKey}`) || ylabel || '');
  const [showEditor, setShowEditor] = useState(false);
  const defaultColors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];

  const renderPlot = (t, xl, yl) => {
    if (!plotData || !plotRef.current || !window.Plotly) return;
    const traces = [];
    plotData.forEach((series, idx) => {
      const color = defaultColors[idx % defaultColors.length];
      const groupName = series.name;
      traces.push({
        x: series.x,
        y: series.y,
        type: 'scatter',
        mode: 'lines+markers',
        name: series.name,
        legendgroup: groupName,
        line: { width: 2, color: color },
        marker: { size: 6 }
      });
      if (showMrpLine && series.mrp !== undefined && series.mrp !== null) {
        traces.push({
          x: [series.mrp, series.mrp],
          y: [0, Math.max(...series.y)],
          type: 'scatter',
          mode: 'lines',
          name: `${series.name} MRP`,
          legendgroup: groupName,
          showlegend: false,
          line: { dash: 'dash', width: 2, color: color }
        });
      }
    });
    window.Plotly.newPlot(plotRef.current, traces, {
      title: t,
      xaxis: { title: xl },
      yaxis: { title: yl },
      template: 'plotly_white',
      height: 400,
      legend: { orientation: 'h', y: -0.2 },
      margin: { t: 40, b: 80, l: 60, r: 20 }
    }, { responsive: true });
  };

  useEffect(() => { if (plotData) renderPlot(title, xLabel, yLabel); }, [plotData, title, xLabel, yLabel]);

  const applyTitles = () => {
    renderPlot(title, xLabel, yLabel);
    localStorage.setItem(`mrp_numbers_title_${titleKey}`, title);
    localStorage.setItem(`mrp_numbers_xlabel_${titleKey}`, xLabel);
    localStorage.setItem(`mrp_numbers_ylabel_${titleKey}`, yLabel);
    setShowEditor(false);
  };

  if (!plotData) return null;
  return (
    <div style={{ marginBottom: '20px', background: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>{title}</h4>
        <button onClick={() => setShowEditor(!showEditor)} style={{ padding: '2px 8px', fontSize: '11px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>✏️ Edit</button>
      </div>
      {showEditor && (
        <div style={{ display: 'flex', gap: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} style={{ flex: 2, fontSize: '12px', padding: '4px' }} />
          <input placeholder="X axis" value={xLabel} onChange={e => setXLabel(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
          <input placeholder="Y axis" value={yLabel} onChange={e => setYLabel(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
          <button onClick={applyTitles} style={{ background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer' }}>Apply</button>
        </div>
      )}
      <div ref={plotRef} style={{ width: '100%', height: '400px' }}></div>
    </div>
  );
};

const MRPNumbers = () => {
  const [step, setStep] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [datasetLoaded, setDatasetLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('cumulative');
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  
  // ========== ADDED: Analysis state variables ==========
  const [savedAnalysisId, setSavedAnalysisId] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState('draft');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(true);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  
  // ========== ADDED: Mode detection ==========
  const mode = new URLSearchParams(window.location.search).get('mode');
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit' || !mode;
  
  const [groups, setGroups] = useState({
    facility: false,
    dates: false,
    segmentation: false,
    status: false,
    target: false
  });
  
  const [formData, setFormData] = useState({
    Facility_ID: null,
    Default_Date: null,
    Closed_Date: null,
    Frequency: { value: 'Monthly', label: 'Monthly' },
    Calibration_segment: null,
    Grade: null,
    Status: null,
    Threshold_MRP: 2
  });

  const frequencyOptions = [
    { value: 'Monthly', label: 'Monthly' },
    { value: 'Quarterly', label: 'Quarterly' },
    { value: 'Annual', label: 'Annual' }
  ];

  const columnOptions = columns.map(c => ({ value: c, label: c }));

  // ========== ADDED: Load saved analysis on mount ==========
  useEffect(() => {
    const analysisId = new URLSearchParams(window.location.search).get('analysis_id');
    if (analysisId && !isNaN(parseInt(analysisId))) {
      loadSavedAnalysis(parseInt(analysisId));
    }
  }, []);

  useEffect(() => {
    if (!window.Plotly) {
      const script = document.createElement('script');
      script.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js';
      document.head.appendChild(script);
    }
  }, []);

  // ========== ADDED: Load saved analysis function ==========
  const loadSavedAnalysis = async (analysisId) => {
    setIsLoadingAnalysis(true);
    try {
      const res = await api.get(`/analyses/${analysisId}`);
      const analysis = res.data;
      
      if (analysis.status) setAnalysisStatus(analysis.status);
      if (analysis.description) setAnalysisDescription(analysis.description);
      setSavedAnalysisId(analysis.id);
      if (analysis.comments && Array.isArray(analysis.comments)) setComments(analysis.comments);
      
      if (analysis.input_parameters) {
        // Restore form data
          if (analysis.input_parameters.dataset_id) {
            setDatasetInfo({
              id: analysis.input_parameters.dataset_id,
              name: analysis.input_parameters.dataset_name,
              rows: analysis.input_parameters.rows,
              columns: analysis.input_parameters.columns
            });
            setColumns(analysis.input_parameters.columns || []);
          }
          
        setFormData({
          Facility_ID: analysis.input_parameters.Facility_ID ? { value: analysis.input_parameters.Facility_ID, label: analysis.input_parameters.Facility_ID } : null,
          Default_Date: analysis.input_parameters.Default_Date ? { value: analysis.input_parameters.Default_Date, label: analysis.input_parameters.Default_Date } : null,
          Closed_Date: analysis.input_parameters.Closed_Date ? { value: analysis.input_parameters.Closed_Date, label: analysis.input_parameters.Closed_Date } : null,
          Frequency: analysis.input_parameters.Frequency ? { value: analysis.input_parameters.Frequency, label: analysis.input_parameters.Frequency } : { value: 'Monthly', label: 'Monthly' },
          Calibration_segment: analysis.input_parameters.Calibration_segment ? { value: analysis.input_parameters.Calibration_segment, label: analysis.input_parameters.Calibration_segment } : null,
          Grade: analysis.input_parameters.Grade ? { value: analysis.input_parameters.Grade, label: analysis.input_parameters.Grade } : null,
          Status: analysis.input_parameters.Status ? { value: analysis.input_parameters.Status, label: analysis.input_parameters.Status } : null,
          Threshold_MRP: analysis.input_parameters.Threshold_MRP || 2
        });
        
        // Restore selected status values
        if (analysis.input_parameters.Status_values) {
          setSelectedStatus(analysis.input_parameters.Status_values.map(v => ({ value: v, label: String(v) })));
        }
      }
      
      if (analysis.output_result) {
        setResults(analysis.output_result);
        setStep(2);
        toast.success('Analysis loaded with results');
      } else if (analysis.input_parameters) {
        setStep(1);
        toast.success('Analysis loaded. Please run the analysis to see results.');
      }
    } catch (err) {
      toast.error('Failed to load analysis');
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  // ========== ADDED: Save Analysis function ==========
  const handleSaveAnalysis = async () => {
    if (!datasetInfo) {
      toast.error('No dataset selected');
      return;
    }
    const projectId = new URLSearchParams(window.location.search).get('project_id');
    if (!projectId) {
      toast.error('No project selected');
      return;
    }
    
    if (!results) {
      toast.error('No results to save. Please run the analysis first.');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        project_id: parseInt(projectId),
        module_name: 'mrp_numbers',
        description: analysisDescription,
        input_parameters: {
          dataset_id: datasetInfo.id,
          dataset_name: datasetInfo.name,
          rows: datasetInfo.rows,
          columns: datasetInfo.columns,
          Facility_ID: formData.Facility_ID?.value || '',
          Default_Date: formData.Default_Date?.value || '',
          Closed_Date: formData.Closed_Date?.value || '',
          Frequency: formData.Frequency?.value || 'Monthly',
          Calibration_segment: formData.Calibration_segment?.value || '',
          Grade: formData.Grade?.value || '',
          Status: formData.Status?.value || '',
          Threshold_MRP: formData.Threshold_MRP,
          Status_values: selectedStatus.map(s => s.value)
        },
        output_result: results,
        status: analysisStatus,
        comments: comments
      };
      const res = await api.post('/analyses/', payload);
      toast.success(`Analysis saved with ID: ${res.data.id}`);
      setSavedAnalysisId(res.data.id);
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save analysis');
    } finally {
      setLoading(false);
    }
  };

  // ========== ADDED: Update Metadata function ==========
  const handleUpdateMetadata = async () => {
    if (!savedAnalysisId) {
      toast.error('No analysis saved yet');
      return;
    }
    setUpdatingMetadata(true);
    try {
      await api.patch(`/analyses/${savedAnalysisId}`, {
        status: analysisStatus,
        description: analysisDescription
      });
      toast.success('Status and description updated');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdatingMetadata(false);
    }
  };

  // ========== FIX 3 & 4: Comments ALWAYS EDITABLE - removed condition on savedAnalysisId ==========
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    const commentObj = {
      id: Date.now(),
      text: newComment,
      created_at: new Date().toISOString(),
      user_id: localStorage.getItem('user_id') || 'user',
      user_email: localStorage.getItem('user_email') || 'Unknown'
    };
    const updatedComments = [...comments, commentObj];
    
    if (savedAnalysisId) {
      setSavingComment(true);
      try {
        await api.patch(`/analyses/${savedAnalysisId}`, { comments: updatedComments });
        setComments(updatedComments);
        setNewComment('');
        toast.success('Comment added');
      } catch (err) {
        toast.error('Failed to add comment');
      } finally {
        setSavingComment(false);
      }
    } else {
      setComments(updatedComments);
      setNewComment('');
      toast.success('Comment added (will be saved when you save the analysis)');
    }
  };

  const hdl = (data) => {
    setColumns(data.columns || []);
    setDatasetInfo(data);
    setDatasetLoaded(true);
    toast.success(`Loaded ${data.rows?.toLocaleString()} rows`);
  };

  const hfc = (f, v) => setFormData(p => ({ ...p, [f]: v }));

  const loadStatusValues = async () => {
    if (!formData.Status || !datasetInfo?.id) return;
    try {
      const res = await api.get(`/api/cart/column-values?dataset_id=${datasetInfo.id}&column=${formData.Status.value}`);
      setStatusOptions(res.data.values.map(v => ({ value: v, label: String(v) })));
    } catch (err) { console.error(err); }
  };

  useEffect(() => { if (formData.Status) loadStatusValues(); }, [formData.Status, datasetInfo]);

  const toggleGroup = (group) => {
    if (isViewMode) return;
    setGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const saveParams = async () => {
    const required = ['Facility_ID', 'Default_Date', 'Closed_Date', 'Status'];
    for (let f of required) {
      if (!formData[f]) {
        toast.error(`Please select ${f}`);
        return;
      }
    }
    if (!formData.Grade && !formData.Calibration_segment) {
      toast.error('Please select either Grade or Calibration segment (or both)');
      return;
    }
    if (selectedStatus.length === 0) {
      toast.error('Please select at least one value for "Unclosed flag"');
      return;
    }
    const payload = {
      Facility_ID: formData.Facility_ID.value,
      Default_Date: formData.Default_Date.value,
      Closed_Date: formData.Closed_Date.value,
      Frequency: formData.Frequency.value,
      Calibration_segment: formData.Calibration_segment?.value || '',
      Grade: formData.Grade?.value || '',
      Status: formData.Status.value,
      Threshold_MRP: formData.Threshold_MRP,
      Status_values: selectedStatus.map(s => s.value)
    };
    setLoading(true);
    try {
      await api.post('/api/mrp-numbers/save-params', payload);
      toast.success('Parameters saved');
      setStep(1);
    } catch (err) {
      toast.error('Failed to save parameters');
    } finally { setLoading(false); }
  };

  const runAnalysis = async () => {
    const payload = {
      Facility_ID: formData.Facility_ID.value,
      Default_Date: formData.Default_Date.value,
      Closed_Date: formData.Closed_Date.value,
      Frequency: formData.Frequency.value,
      Calibration_segment: formData.Calibration_segment?.value || '',
      Grade: formData.Grade?.value || '',
      Status: formData.Status.value,
      Threshold_MRP: formData.Threshold_MRP,
      Status_values: selectedStatus.map(s => s.value)
    };
    setLoading(true);
    try {
      const response = await api.post('/api/mrp-numbers/run-analysis', payload);
      setResults(response.data.results);
      toast.success(response.data.message);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally { setLoading(false); }
  };

  
  const saveMRPColumn = async () => {
    const colName = prompt('Enter new column name for MRP:', 'mrp_value');
    if (!colName) return;
    setLoading(true);
    try {
      await api.post('/api/mrp-numbers/save-mrp', { new_column_name: colName });
      toast.success(`MRP saved as '${colName}'`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save MRP');
    } finally {
      setLoading(false);
    }
  };


  const exportResults = async () => {
    const customTitles = {
      cumulative_title: localStorage.getItem('mrp_numbers_title_cumulative') || '',
      cumulative_xlabel: localStorage.getItem('mrp_numbers_xlabel_cumulative') || 'Time in Default',
      cumulative_ylabel: localStorage.getItem('mrp_numbers_ylabel_cumulative') || 'Cumulative Number of Defaults',
      mrp_title: localStorage.getItem('mrp_numbers_title_mrp') || '',
      mrp_xlabel: localStorage.getItem('mrp_numbers_xlabel_mrp') || 'Time in Default',
      mrp_ylabel: localStorage.getItem('mrp_numbers_ylabel_mrp') || '% Cumulative Defaults'
    };
    try {
      const response = await api.post('/api/mrp-numbers/export-with-titles', { custom_titles: customTitles }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mrp_numbers_results.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export started');
    } catch (err) { toast.error('Failed to export results'); }
  };

  const sc = (t) => {
    if (t === 0) setStep(0);
    else if (t === 1 && datasetLoaded && formData.Facility_ID) setStep(1);
    else if (t === 2 && results) setStep(2);
  };

  const cs = {
    control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px', borderColor: '#e0e0e0' }),
    valueContainer: (b) => ({ ...b, padding: '0 8px' }),
    placeholder: (b) => ({ ...b, fontSize: '13px', color: '#95a5a6' }),
    menu: (b) => ({ ...b, zIndex: 9999, position: 'absolute' }),
    menuPortal: (b) => ({ ...b, zIndex: 9999 })
  };

  const cumulativeCurves = results?.interactive?.avg_curves?.map(curve => ({ name: curve.grade, x: curve.x, y: curve.y })) || [];
  const mrpCurves = results?.interactive?.pct_curves?.map(curve => ({ name: curve.grade, x: curve.x, y: curve.y, mrp: curve.mrp })) || [];

  const groupStyle = {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    marginBottom: '16px',
    overflow: 'visible',
    position: 'relative'
  };
  
  const groupHeaderStyle = {
    background: '#f5f7fa',
    padding: '12px 16px',
    cursor: isViewMode ? 'not-allowed' : 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '14px',
    color: '#2c3e50',
    opacity: isViewMode ? 0.6 : 1
  };
  
  const groupContentStyle = {
    padding: '16px',
    background: 'white',
    overflow: 'visible'
  };

  const gs = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' };
  const ghs = { background: '#f5f7fa', padding: '12px 16px', fontWeight: 600, fontSize: '14px', color: '#2c3e50' };
  const gcs = { padding: '16px', background: 'white' };

  if (isLoadingAnalysis) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading analysis...</div>
      </div>
    );
  }

  return (
    <DiscretizationLayout title="Max Recovery Period - Numbers" step={step} onStepClick={sc} datasetLoaded={datasetLoaded} results={results}>
      
      {/* ========== ADDED: View mode banner ========== */}
      {isViewMode && results && (
        <div style={{ background: '#e8f8f5', padding: '8px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#1abc9c' }}>👁️ Viewing saved analysis (read-only)</span>
          <button
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.set('mode', 'edit');
              window.location.href = url.toString();
            }}
            style={{ padding: '4px 12px', fontSize: '12px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Modify
          </button>
        </div>
      )}

      {step === 0 && (
        <>
          <DatasetSelector projectId={new URLSearchParams(window.location.search).get("project_id")} apiPrefix="/api/mrp-numbers" onDatasetLoaded={hdl} />
          
          {/* Show dataset name when loading a saved analysis */}
          {savedAnalysisId && datasetInfo && (
            <div style={{ 
              marginTop: '16px', 
              marginBottom: '16px', 
              padding: '12px', 
              background: '#e8f8f5', 
              borderRadius: '8px',
              borderLeft: '4px solid #1abc9c'
            }}>
              <div style={{ fontSize: '13px', color: '#2c3e50', fontWeight: 500 }}>
                📌 Dataset used for this analysis:
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#1abc9c', marginTop: '4px' }}>
                {datasetInfo.name}
              </div>
              <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                {datasetInfo.rows?.toLocaleString()} rows • {datasetInfo.columns?.length || 0} columns
              </div>
            </div>
          )}
          {columns.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              {/* 1. Facility identifier */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('facility')}>
                  <span>🏢 1. Facility identifier</span>
                  <span>{groups.facility ? '−' : '+'}</span>
                </div>
                {groups.facility && (
                  <div style={groupContentStyle}>
                    <div style={{ maxWidth: '400px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Facility ID *</label>
                      <Select options={columnOptions} value={formData.Facility_ID} onChange={v => hfc('Facility_ID', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select facility ID column..." isDisabled={isViewMode} />
                    </div>
                  </div>
                )}
              </div>

              {/* 2. Key dates */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('dates')}>
                  <span>📅 2. Key dates</span>
                  <span>{groups.dates ? '−' : '+'}</span>
                </div>
                {groups.dates && (
                  <div style={groupContentStyle}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Claims Date *</label>
                        <Select options={columnOptions} value={formData.Default_Date} onChange={v => hfc('Default_Date', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select claims date column..." isDisabled={isViewMode} />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Closed Date *</label>
                        <Select options={columnOptions} value={formData.Closed_Date} onChange={v => hfc('Closed_Date', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select closed date column..." isDisabled={isViewMode} />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Frequency *</label>
                        <Select options={frequencyOptions} value={formData.Frequency} onChange={v => hfc('Frequency', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select frequency..." isDisabled={isViewMode} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 3. Segmentation */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('segmentation')}>
                  <span>🎯 3. Segmentation</span>
                  <span>{groups.segmentation ? '−' : '+'}</span>
                </div>
                {groups.segmentation && (
                  <div style={groupContentStyle}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Calibration segment (optional)</label>
                        <Select options={columnOptions} value={formData.Calibration_segment} onChange={v => hfc('Calibration_segment', v)} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select calibration segment..." isDisabled={isViewMode} />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Grade (optional)</label>
                        <Select options={columnOptions} value={formData.Grade} onChange={v => hfc('Grade', v)} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select grade column..." isDisabled={isViewMode} />
                      </div>
                    </div>
                    <p style={{ fontSize: '11px', color: '#e74c3c', marginTop: '8px' }}>⚠️ At least one of Grade or Calibration segment must be selected</p>
                  </div>
                )}
              </div>

              {/* 4. Default status */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('status')}>
                  <span>⚠️ 4. Default status</span>
                  <span>{groups.status ? '−' : '+'}</span>
                </div>
                {groups.status && (
                  <div style={groupContentStyle}>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Status *</label>
                      <Select options={columnOptions} value={formData.Status} onChange={v => hfc('Status', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select status column..." isDisabled={isViewMode} />
                    </div>
                    {formData.Status && statusOptions.length > 0 && (
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Unclosed flag * (select values to include)</label>
                        <Select options={statusOptions} isMulti value={selectedStatus} onChange={setSelectedStatus} styles={cs} menuPortalTarget={document.body} placeholder="Select status values..." isDisabled={isViewMode} />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 5. Target variable */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('target')}>
                  <span>📊 5. Target variable</span>
                  <span>{groups.target ? '−' : '+'}</span>
                </div>
                {groups.target && (
                  <div style={groupContentStyle}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Threshold MRP (%)</label>
                      <input type="number" value={formData.Threshold_MRP} onChange={e => hfc('Threshold_MRP', parseInt(e.target.value))} style={{ width: '200px', padding: '8px', fontSize: '13px', border: '1px solid #e0e0e0', borderRadius: '4px' }} disabled={isViewMode} />
                      <p style={{ fontSize: '10px', color: '#7f8c8d', marginTop: '4px' }}>Percentage threshold for MRP calculation (default: 2%)</p>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button onClick={saveParams} disabled={loading || isViewMode} style={{ padding: '8px 28px', background: loading || isViewMode ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: loading || isViewMode ? 'not-allowed' : 'pointer', fontSize: '13px' }}>{loading ? "Saving..." : "Continue →"}</button>
              </div>
            </div>
          )}
        </>
      )}

      {step === 1 && (
        <div>
          <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Review Parameters</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '8px', fontSize: '13px' }}>
              <p><strong>Facility ID:</strong> {formData.Facility_ID?.label}</p>
              <p><strong>Claims Date:</strong> {formData.Default_Date?.label}</p>
              <p><strong>Closed Date:</strong> {formData.Closed_Date?.label}</p>
              <p><strong>Frequency:</strong> {formData.Frequency?.label}</p>
              <p><strong>Grade:</strong> {formData.Grade?.label || "(not provided)"}</p>
              <p><strong>Status:</strong> {formData.Status?.label}</p>
              <p><strong>Threshold MRP:</strong> {formData.Threshold_MRP}%</p>
              {formData.Calibration_segment && <p><strong>Calibration segment:</strong> {formData.Calibration_segment.label}</p>}
              <p><strong>Unclosed flag filter:</strong> {selectedStatus.map(s => s.label).join(', ') || 'None'}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => setStep(0)} disabled={isViewMode} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={runAnalysis} disabled={loading || isViewMode} style={{ padding: '8px 24px', background: loading || isViewMode ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{loading ? "Running..." : "Run Analysis →"}</button>
          </div>
        </div>
      )}

      {step === 2 && results && (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #e0e0e0' }}>
            <button onClick={() => setActiveTab('cumulative')} style={{ padding: '8px 16px', fontSize: '13px', background: activeTab === 'cumulative' ? '#1abc9c' : 'transparent', color: activeTab === 'cumulative' ? 'white' : '#2c3e50', border: 'none', borderBottom: activeTab === 'cumulative' ? '2px solid #1abc9c' : 'none', cursor: 'pointer' }}>Cumulative Defaults</button>
            <button onClick={() => setActiveTab('mrp')} style={{ padding: '8px 16px', fontSize: '13px', background: activeTab === 'mrp' ? '#1abc9c' : 'transparent', color: activeTab === 'mrp' ? 'white' : '#2c3e50', border: 'none', borderBottom: activeTab === 'mrp' ? '2px solid #1abc9c' : 'none', cursor: 'pointer' }}>MRP Curve</button>
            <button onClick={() => setActiveTab('tables')} style={{ padding: '8px 16px', fontSize: '13px', background: activeTab === 'tables' ? '#1abc9c' : 'transparent', color: activeTab === 'tables' ? 'white' : '#2c3e50', border: 'none', borderBottom: activeTab === 'tables' ? '2px solid #1abc9c' : 'none', cursor: 'pointer' }}>Data Tables</button>
          </div>

          {activeTab === 'cumulative' && (
            <LineChart plotData={cumulativeCurves} defaultTitle={`Average Cumulative Defaults by Grade (${results.meta?.Frequency || 'Monthly'})`} xlabel={`Time in Default (${results.meta?.Frequency || 'Monthly'})`} ylabel="Cumulative Number of Defaults" titleKey="cumulative" showMrpLine={false} />
          )}

          {activeTab === 'mrp' && (
            <LineChart plotData={mrpCurves} defaultTitle={`Cumulative % Defaults with MRP (${results.meta?.Frequency || 'Monthly'})`} xlabel={`Time in Default (${results.meta?.Frequency || 'Monthly'})`} ylabel="% Cumulative Defaults" titleKey="mrp" showMrpLine={true} />
          )}

          {activeTab === 'tables' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>Average Cumulative Defaults</h4>
                <div className="table-scroll" style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white' }}>
                      <tr>
                        {results.tables?.avg_cumulated?.length > 0 && Object.keys(results.tables.avg_cumulated[0]).map(k => (
                          <th key={k} style={{ padding: '8px' }}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.tables?.avg_cumulated?.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                          {Object.values(row).map((val, j) => (
                            <td key={j} style={{ padding: '6px' }}>{typeof val === 'number' ? val.toFixed(4) : val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>Percentage Cumulative Defaults (MRP)</h4>
                <div className="table-scroll" style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white' }}>
                      <tr>
                        {results.tables?.pct_cumulative?.length > 0 && Object.keys(results.tables.pct_cumulative[0]).map(k => (
                          <th key={k} style={{ padding: '8px' }}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.tables?.pct_cumulative?.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                          {Object.values(row).map((val, j) => (
                            <td key={j} style={{ padding: '6px' }}>{typeof val === 'number' ? val.toFixed(4) : val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>MRP Values</h4>
                <div className="table-scroll" style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white' }}>
                      <tr>
                        {results.tables?.mrp?.length > 0 && Object.keys(results.tables.mrp[0]).map(k => (
                          <th key={k} style={{ padding: '8px' }}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.tables?.mrp?.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                          {Object.values(row).map((val, j) => (
                            <td key={j} style={{ padding: '6px' }}>{val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ========== FIX 3: Comments Section - ALWAYS EDITABLE ========== */}
          <div style={{ ...gs, marginTop: '20px' }}>
            <div 
              style={{ 
                ...ghs, 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none'
              }}
              onClick={() => setIsCommentsExpanded(!isCommentsExpanded)}
            >
              <span>💬 Comments & Discussions ({comments.length})</span>
              <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {isCommentsExpanded ? '−' : '+'}
              </span>
            </div>
            
            {isCommentsExpanded && (
              <div style={gcs}>
                <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '16px' }}>
                  {comments.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                      No comments yet. Be the first to add a comment.
                    </Typography>
                  ) : (
                    comments.map((comment) => (
                      <div
                        key={comment.id}
                        style={{
                          padding: '10px 12px',
                          marginBottom: '8px',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          borderLeft: '3px solid #1abc9c'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#2c3e50' }}>
                            {comment.user_email || comment.user_id || 'Unknown'}
                          </span>
                          <span style={{ fontSize: '10px', color: '#95a5a6' }}>
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#34495e', whiteSpace: 'pre-wrap' }}>
                          {comment.text}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                {/* Comment input - ALWAYS VISIBLE (removed savedAnalysisId condition) */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment or question..."
                    style={{
                      flex: 1,
                      padding: '10px',
                      fontSize: '13px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      resize: 'vertical',
                      minHeight: '60px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={savingComment || !newComment.trim()}
                    style={{
                      padding: '8px 20px',
                      background: (savingComment || !newComment.trim()) ? '#95a5a6' : '#1abc9c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (savingComment || !newComment.trim()) ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      height: 'fit-content'
                    }}
                  >
                    {savingComment ? 'Sending...' : 'Add Comment'}
                  </button>
                </div>
                
                {/* Hint for unsaved comments */}
                {!savedAnalysisId && comments.length > 0 && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    💡 Comments will be permanently saved when you click "Save Analysis"
                  </Typography>
                )}
              </div>
            )}
          </div>

          {/* ========== FIX 1 & 2: Professional Action Area - SINGLE Back and Export buttons ========== */}
          <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <MuiSelect
                  value={analysisStatus}
                  onChange={(e) => setAnalysisStatus(e.target.value)}
                  label="Status"
                  disabled={isViewMode}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="validated">Validated</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </MuiSelect>
              </FormControl>
            </div>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={analysisDescription}
              onChange={(e) => setAnalysisDescription(e.target.value)}
              placeholder="Describe the purpose, methodology, or key findings of this MRP numbers analysis..."
              disabled={isViewMode}
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => setStep(1)}
                disabled={isViewMode}
                size="small"
              >
                ← Back
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => { 
                  setStep(0); 
                  setColumns([]);
                  setResults(null); 
                  setDatasetInfo(null);
                  setDatasetLoaded(false);
                  setAnalysisStatus('draft'); 
                  setAnalysisDescription(''); 
                  setSavedAnalysisId(null); 
                  setComments([]);
                  setSelectedStatus([]);
                  setStatusOptions([]);
                  setFormData({
                    Facility_ID: null,
                    Default_Date: null,
                    Closed_Date: null,
                    Frequency: { value: 'Monthly', label: 'Monthly' },
                    Calibration_segment: null,
                    Grade: null,
                    Status: null,
                    Threshold_MRP: 2
                  });
                  setGroups({
                    facility: false,
                    dates: false,
                    segmentation: false,
                    status: false,
                    target: false
                  });
                }}
                disabled={isViewMode}
                size="small"
              >
                New Analysis
              </Button>
              
              <Button
                variant="contained"
                onClick={exportResults}
                disabled={isViewMode}
                size="small"
                sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' } }}
              >
                📥 Export
              </Button>
              
              <Button
                variant="contained"
                onClick={saveMRPColumn}
                disabled={loading || isViewMode}
                size="small"
                sx={{ bgcolor: loading || isViewMode ? '#95a5a6' : '#27ae60', '&:hover': { bgcolor: '#219a52' } }}
              >
                {loading ? 'Saving...' : '💾 Save MRP'}
              </Button>
              
              {isEditMode && (
                <Button
                  variant="contained"
                  onClick={handleSaveAnalysis}
                  disabled={!results || loading}
                  size="small"
                  sx={{ bgcolor: results && !loading ? '#1abc9c' : '#95a5a6', '&:hover': { bgcolor: '#16a085' } }}
                >
                  {loading ? 'Saving...' : '💾 Save Analysis'}
                </Button>
              )}
            </Box>
          </div>
          
          {/* ========== REMOVED: Duplicate original buttons ========== */}
        </div>
      )}
    </DiscretizationLayout>
  );
};

export default MRPNumbers;